// The scheme allowlist for a link target that came from MODEL- or USER-authored text.
//
// Making such text clickable is exactly the moment an injection surface can appear, so the rule is a
// closed allowlist rather than a blocklist: a target whose scheme is not named here is not a link at
// all (the label still renders, as ordinary text).
//
// Why this file exists at all: react-markdown ships its own `defaultUrlTransform` whose allowlist is
// `http|https|ircs?|mailto|xmpp`. That correctly rejects script- and inline-data schemes — but it also
// rejects `orion:`, so every `[label](orion://time/task/123)` the assistant emits was rewritten to
// `href=""` and rendered as a dead link that reloaded the page. `resolveInAppPath('')` then returned
// null and `standaloneEntityLink` saw an empty href, so neither the in-app routing branch in
// `components/Markdown.tsx` nor the entity-chip treatment could ever fire. Replacing the transform with
// this one keeps every rejection the default made and adds back the app's own scheme.
//
// Kept 1:1 with the native allowlists (`MarkdownMessage.isSafeLinkURL` on iOS, `isSafeLinkUrl` on
// Android) — change one, change all three.
const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:', 'orion:']);

/**
 * react-markdown's `urlTransform`. Returns the URL when its scheme is allowed (or when it has no scheme
 * at all — a relative/anchor href), and the empty string otherwise.
 *
 * The "is there a scheme" test mirrors react-markdown's own: a `:` only introduces a scheme when it
 * comes before the first `/`, `?` and `#`, so `./a:b`, `?x=a:b` and `#a:b` stay relative.
 */
export function safeUrl(value: string): string {
  const colon = value.indexOf(':');
  if (colon === -1) return value;
  const slash = value.indexOf('/');
  const questionMark = value.indexOf('?');
  const numberSign = value.indexOf('#');
  const beforeAnyDelimiter =
    (slash === -1 || colon < slash) &&
    (questionMark === -1 || colon < questionMark) &&
    (numberSign === -1 || colon < numberSign);
  if (!beforeAnyDelimiter) return value;
  return SAFE_SCHEMES.has(value.slice(0, colon + 1).toLowerCase()) ? value : '';
}

/** True when `value` is a link target we are willing to render as an activatable link. */
export function isSafeUrl(value: string | undefined | null): boolean {
  return !!value && safeUrl(value) === value;
}
